import { Injectable, inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { ClientRenderRuntimeBridgeService } from '../../../core/services/client-render-runtime-bridge.service';
import { DriveService } from './drive.service';

export interface ClientAnalyzePayload {
  audioKey: string;
  videoInfoJson: string;
  hasAudio: boolean;
  frameKeysJson?: string;
  runtime: string;
  durationSeconds: number;
}

export type ProcessingAnalysisMode = 'speech' | 'visual';

type ProgressCallback = (message: string) => void;

export class ClientRuntimeLimitError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ClientRuntimeLimitError';
  }
}

export class ClientRuntimeUnavailableError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ClientRuntimeUnavailableError';
  }
}

@Injectable({
  providedIn: 'root'
})
export class ClientAudioAnalyzeService {
  private readonly driveService = inject(DriveService);
  private readonly renderRuntime = inject(ClientRenderRuntimeBridgeService);

  async prepare(
    file: File,
    progress?: ProgressCallback,
    jobId?: string,
    mode: ProcessingAnalysisMode = 'speech'
  ): Promise<ClientAnalyzePayload | null> {
    if (!this.isVideoFile(file)) {
      return null;
    }

    progress?.('Checking local runtime limit...');
    await this.renderRuntime.loadRuntimeLimits();

    const runtime = this.renderRuntime.runtimeKind();
    const mediaInfo = await this.getMediaInfo(file);
    const limits = await this.renderRuntime.loadRuntimeLimits();
    const maxSeconds = this.renderRuntime.maxSeconds(runtime);
    if (mediaInfo.durationSeconds > maxSeconds) {
      throw new ClientRuntimeLimitError(this.limitMessage(mediaInfo.durationSeconds, runtime, limits));
    }

    if (mode === 'visual') {
      progress?.('Rendering visual frames locally. Do not close this window...');
      const frameKeys = await this.extractAndUploadFrames(file, mediaInfo, progress, jobId);

      return {
        audioKey: '',
        videoInfoJson: this.buildVideoInfoJson(mediaInfo, false),
        hasAudio: false,
        frameKeysJson: JSON.stringify(frameKeys),
        runtime,
        durationSeconds: mediaInfo.durationSeconds
      };
    }

    if (runtime === 'desktop') {
      const desktopResult = await this.tryDesktopExtraction(file, mediaInfo.durationSeconds, progress, jobId);
      if (desktopResult) {
        return {
          ...desktopResult,
          videoInfoJson: this.buildVideoInfoJson(mediaInfo, true),
          runtime,
          durationSeconds: mediaInfo.durationSeconds
        };
      }

      throw new ClientRuntimeUnavailableError(
        'Desktop audio extraction is unavailable. Please use the HeyJivu desktop app with FFmpeg support, then try again.'
      );
    }

    if (runtime === 'mobile') {
      throw new ClientRuntimeUnavailableError(
        'Mobile audio extraction needs the HeyJivu mobile app runtime. Please use Chrome for videos up to the browser limit, or the desktop app for longer videos.'
      );
    }

    progress?.('Extracting audio locally. Do not close this window...');
    const audioFile = await this.extractBrowserWav(file);
    progress?.('Uploading extracted audio to temp storage...');
    const audioKey = await this.uploadAudioFile(audioFile, file.name, jobId);

    return {
      audioKey,
      videoInfoJson: this.buildVideoInfoJson(mediaInfo, true),
      hasAudio: true,
      frameKeysJson: '[]',
      runtime,
      durationSeconds: mediaInfo.durationSeconds
    };
  }

  private async tryDesktopExtraction(
    file: File,
    durationSeconds: number,
    progress?: ProgressCallback,
    jobId?: string
  ): Promise<Pick<ClientAnalyzePayload, 'audioKey' | 'hasAudio' | 'frameKeysJson'> | null> {
    const desktop = window.heyjivuDesktop;
    const sourcePath = this.getDesktopFilePath(file);
    const ffmpeg = desktop?.ffmpeg;
    const uploadFile = desktop?.review?.uploadFile;

    if (!sourcePath || !ffmpeg?.startManifestJob || !ffmpeg.onProgress || !uploadFile) {
      return null;
    }

    progress?.('Extracting audio with desktop FFmpeg. Do not close this window...');
    const job = await ffmpeg.startManifestJob({
      mode: 'render',
      inputs: [sourcePath],
      outputFileName: 'audio.wav',
      overwrite: true,
      expectedDurationSeconds: durationSeconds,
      ffmpegArgs: ['-vn', '-acodec', 'pcm_s16le', '-ar', '16000', '-ac', '1']
    });

    if (!job.jobId || !job.outputPath) return null;
    await this.waitForDesktopJob(job.jobId, ffmpeg.onProgress, progress);

    const key = this.tempAudioKey(file.name, jobId);
    const { url, filePath } = await firstValueFrom(this.driveService.getPresignedUploadUrl(key, 'audio/wav', 'client-runtime-temp'));
    progress?.('Uploading extracted audio to temp storage...');
    const uploadResult = await uploadFile({ url, filePath: job.outputPath, contentType: 'audio/wav' });
    if (!uploadResult.ok) {
      throw new Error(`Desktop audio upload failed with HTTP ${uploadResult.status}.`);
    }

    return { audioKey: filePath || key, hasAudio: true, frameKeysJson: '[]' };
  }

  private waitForDesktopJob(
    jobId: string,
    onProgress: (listener: (payload: { jobId?: string; phase?: string; percent?: number; message?: string }) => void) => () => void,
    progress?: ProgressCallback
  ): Promise<void> {
    return new Promise((resolve, reject) => {
      const cleanup = onProgress(payload => {
        if (payload.jobId !== jobId) return;
        if (payload.phase === 'progress' && Number.isFinite(payload.percent)) {
          progress?.(`Extracting audio locally (${Math.round(payload.percent ?? 0)}%). Do not close this window...`);
        }
        if (payload.phase === 'complete') {
          cleanup();
          resolve();
        }
        if (payload.phase === 'error' || payload.phase === 'canceled') {
          cleanup();
          reject(new Error(payload.message || 'Desktop audio extraction failed.'));
        }
      });
    });
  }

  private async extractAndUploadFrames(
    file: File,
    mediaInfo: { durationSeconds: number; width: number; height: number; formatName: string },
    progress?: ProgressCallback,
    jobId?: string
  ): Promise<string[]> {
    const frameTimes = this.visualFrameTimes(mediaInfo.durationSeconds);
    const folderId = this.tempBatchFolder(file.name, jobId);
    const keys: string[] = [];

    for (let index = 0; index < frameTimes.length; index++) {
      progress?.(`Rendering visual frame ${index + 1}/${frameTimes.length} locally. Do not close this window...`);
      const frameFile = await this.captureBrowserFrame(file, frameTimes[index], index);

      progress?.(`Uploading visual frame ${index + 1}/${frameTimes.length} to temp storage...`);
      keys.push(await this.uploadFrameFile(frameFile, folderId, index));
    }

    return keys;
  }

  private visualFrameTimes(durationSeconds: number): number[] {
    const duration = Number.isFinite(durationSeconds) && durationSeconds > 0 ? durationSeconds : 24;
    const count = duration <= 20 ? 4 : duration <= 90 ? 6 : 8;
    const guard = Math.min(0.75, Math.max(0.1, duration * 0.03));
    const start = Math.min(guard, Math.max(0, duration - 0.1));
    const end = Math.max(start, duration - guard);

    if (end <= start) {
      return [start];
    }

    return Array.from({ length: count }, (_, index) => start + ((end - start) * index) / (count - 1));
  }

  private captureBrowserFrame(file: File, timeSeconds: number, index: number): Promise<File> {
    return new Promise((resolve, reject) => {
      const url = URL.createObjectURL(file);
      const video = document.createElement('video');
      const canvas = document.createElement('canvas');
      const timeout = window.setTimeout(() => {
        cleanup();
        reject(new Error('Timed out while rendering visual frames locally.'));
      }, 20000);

      const cleanup = () => {
        window.clearTimeout(timeout);
        URL.revokeObjectURL(url);
        video.removeAttribute('src');
        video.load();
      };

      video.preload = 'auto';
      video.muted = true;
      video.playsInline = true;
      video.onloadedmetadata = () => {
        const duration = Number.isFinite(video.duration) ? video.duration : timeSeconds;
        video.currentTime = Math.max(0, Math.min(timeSeconds, Math.max(0, duration - 0.1)));
      };
      video.onseeked = () => {
        const width = video.videoWidth || 1280;
        const height = video.videoHeight || 720;
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          cleanup();
          reject(new Error('Browser canvas is unavailable for visual analysis.'));
          return;
        }

        ctx.drawImage(video, 0, 0, width, height);
        canvas.toBlob(blob => {
          cleanup();
          if (!blob) {
            reject(new Error('Unable to encode visual analysis frame.'));
            return;
          }

          resolve(new File([blob], `frame_${String(index + 1).padStart(3, '0')}.jpg`, { type: 'image/jpeg' }));
        }, 'image/jpeg', 0.86);
      };
      video.onerror = () => {
        cleanup();
        reject(new Error('Unable to render visual frames from this video.'));
      };
      video.src = url;
      video.load();
    });
  }

  private async getMediaInfo(file: File): Promise<{ durationSeconds: number; width: number; height: number; formatName: string }> {
    const sourcePath = this.getDesktopFilePath(file);
    if (sourcePath && window.heyjivuDesktop?.ffmpeg?.probeMedia) {
      try {
        const probe = await window.heyjivuDesktop.ffmpeg.probeMedia(sourcePath) as {
          format?: { duration?: string | number; format_name?: string };
          streams?: Array<{ codec_type?: string; width?: number; height?: number }>;
        };
        const videoStream = probe.streams?.find(stream => stream.codec_type === 'video');
        return {
          durationSeconds: Number(probe.format?.duration || 0),
          width: Number(videoStream?.width || 0),
          height: Number(videoStream?.height || 0),
          formatName: probe.format?.format_name || file.type || ''
        };
      } catch {
        // Browser metadata is a safe fallback for desktop file inputs too.
      }
    }

    return this.getBrowserMediaInfo(file);
  }

  private getDesktopFilePath(file: File): string | null {
    const legacyPath = (file as File & { path?: string }).path;
    if (legacyPath) return legacyPath;

    try {
      return window.heyjivuDesktop?.fileSystem?.getPathForFile?.(file) || null;
    } catch {
      return null;
    }
  }

  private getBrowserMediaInfo(file: File): Promise<{ durationSeconds: number; width: number; height: number; formatName: string }> {
    return new Promise((resolve, reject) => {
      const url = URL.createObjectURL(file);
      const video = document.createElement('video');
      video.preload = 'metadata';
      video.onloadedmetadata = () => {
        URL.revokeObjectURL(url);
        resolve({
          durationSeconds: Number.isFinite(video.duration) ? video.duration : 0,
          width: video.videoWidth || 0,
          height: video.videoHeight || 0,
          formatName: file.type || ''
        });
      };
      video.onerror = () => {
        URL.revokeObjectURL(url);
        reject(new Error('Unable to read video metadata.'));
      };
      video.src = url;
    });
  }

  private async extractBrowserWav(file: File): Promise<File> {
    const AudioContextCtor = window.AudioContext || (window as Window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!AudioContextCtor) {
      throw new Error('Browser audio extraction is unavailable.');
    }

    const audioContext = new AudioContextCtor() as AudioContext;
    try {
      const buffer = await file.arrayBuffer();
      const decoded = await audioContext.decodeAudioData(buffer.slice(0));
      const wav = this.encodeWav(decoded);
      return new File([wav], 'audio.wav', { type: 'audio/wav' });
    } finally {
      await audioContext.close().catch(() => undefined);
    }
  }

  private encodeWav(buffer: AudioBuffer): Blob {
    const sourceChannels = Math.max(1, buffer.numberOfChannels || 1);
    const sampleRate = 16000;
    const channels = 1;
    const frameCount = Math.max(1, Math.ceil(buffer.duration * sampleRate));
    const bytesPerSample = 2;
    const blockAlign = channels * bytesPerSample;
    const dataSize = frameCount * blockAlign;
    const arrayBuffer = new ArrayBuffer(44 + dataSize);
    const view = new DataView(arrayBuffer);

    this.writeString(view, 0, 'RIFF');
    view.setUint32(4, 36 + dataSize, true);
    this.writeString(view, 8, 'WAVE');
    this.writeString(view, 12, 'fmt ');
    view.setUint32(16, 16, true);
    view.setUint16(20, 1, true);
    view.setUint16(22, channels, true);
    view.setUint32(24, sampleRate, true);
    view.setUint32(28, sampleRate * blockAlign, true);
    view.setUint16(32, blockAlign, true);
    view.setUint16(34, bytesPerSample * 8, true);
    this.writeString(view, 36, 'data');
    view.setUint32(40, dataSize, true);

    const channelData = Array.from({ length: sourceChannels }, (_, index) => buffer.getChannelData(index));
    const sourceRate = buffer.sampleRate || sampleRate;
    let offset = 44;
    for (let frame = 0; frame < frameCount; frame++) {
      const sourcePosition = frame * sourceRate / sampleRate;
      const sourceIndex = Math.min(buffer.length - 1, Math.floor(sourcePosition));
      const nextIndex = Math.min(buffer.length - 1, sourceIndex + 1);
      const mix = sourcePosition - sourceIndex;
      let sample = 0;

      for (let channel = 0; channel < sourceChannels; channel++) {
        const current = channelData[channel][sourceIndex] || 0;
        const next = channelData[channel][nextIndex] || current;
        sample += current + (next - current) * mix;
      }

      sample = Math.max(-1, Math.min(1, sample / sourceChannels));
      view.setInt16(offset, sample < 0 ? sample * 0x8000 : sample * 0x7fff, true);
      offset += bytesPerSample;
    }

    return new Blob([arrayBuffer], { type: 'audio/wav' });
  }

  private writeString(view: DataView, offset: number, value: string): void {
    for (let i = 0; i < value.length; i++) {
      view.setUint8(offset + i, value.charCodeAt(i));
    }
  }

  private async uploadAudioFile(audioFile: File, sourceName: string, jobId?: string): Promise<string> {
    const key = this.tempAudioKey(sourceName, jobId);
    const result = await firstValueFrom(this.driveService.uploadDirectStorageFile(
      audioFile,
      key,
      'audio/wav',
      'audio.wav',
      'client-runtime-temp'
    ));
    return result.filePath;
  }

  private async uploadFrameFile(frameFile: File, folderId: string, index: number): Promise<string> {
    const key = this.tempFrameKey(folderId, index);
    const result = await firstValueFrom(this.driveService.uploadDirectStorageFile(
      frameFile,
      key,
      'image/jpeg',
      frameFile.name,
      'client-runtime-temp'
    ));
    return result.filePath;
  }

  private tempAudioKey(sourceName: string, jobId?: string): string {
    const dateFolder = new Date().toISOString().slice(0, 10);
    const jobShort = this.shortId(jobId);
    if (jobShort) {
      return `client-runtime-temp/${dateFolder}/${jobShort}/audio.wav`;
    }

    return `client-runtime-temp/${dateFolder}/${crypto.randomUUID()}-${this.safeBaseName(sourceName)}/audio.wav`;
  }

  private tempFrameKey(folderId: string, index: number): string {
    return `${folderId}/frames/frame_${String(index + 1).padStart(3, '0')}.jpg`;
  }

  private tempBatchFolder(sourceName: string, jobId?: string): string {
    const dateFolder = new Date().toISOString().slice(0, 10);
    const jobShort = this.shortId(jobId);
    if (jobShort) {
      return `client-runtime-temp/${dateFolder}/${jobShort}`;
    }

    return `client-runtime-temp/${dateFolder}/${crypto.randomUUID()}-${this.safeBaseName(sourceName)}`;
  }

  private shortId(id?: string): string {
    return (id || '').replace(/[^a-f0-9]/gi, '').toLowerCase().slice(0, 8);
  }

  private buildVideoInfoJson(
    info: { durationSeconds: number; width: number; height: number; formatName: string },
    hasAudio: boolean
  ): string {
    const streams: Array<{ codec_type: string; width?: number; height?: number }> = [
      {
        codec_type: 'video',
        width: info.width,
        height: info.height
      }
    ];

    if (hasAudio) {
      streams.push({ codec_type: 'audio' });
    }

    return JSON.stringify({
      format: {
        duration: info.durationSeconds,
        format_name: info.formatName
      },
      streams
    });
  }

  private isVideoFile(file: File): boolean {
    return file.type.startsWith('video/')
      || /\.(mp4|mov|m4v|webm|mkv|avi)$/i.test(file.name);
  }

  private safeBaseName(name: string): string {
    return name.replace(/\.[^.]+$/, '').replace(/[^a-z0-9_-]+/gi, '-').replace(/^-+|-+$/g, '').slice(0, 48) || 'video';
  }

  private limitMessage(
    durationSeconds: number,
    runtime: string,
    limits: { browserMaxSeconds: number; mobileMaxSeconds: number; desktopMaxSeconds: number }
  ): string {
    const duration = this.formatLimit(durationSeconds);
    const browserLimit = this.formatLimit(limits.browserMaxSeconds);
    const mobileLimit = this.formatLimit(limits.mobileMaxSeconds);
    const desktopLimit = this.formatLimit(limits.desktopMaxSeconds);

    if (runtime === 'browser') {
      if (durationSeconds <= limits.mobileMaxSeconds) {
        return `This video is ${duration}. Browser uploads can process up to ${browserLimit}. Please download the mobile app for videos up to ${mobileLimit}.`;
      }

      if (durationSeconds <= limits.desktopMaxSeconds) {
        return `This video is ${duration}. For videos longer than ${mobileLimit}, please download the desktop app for processing up to ${desktopLimit}.`;
      }

      return `This video is ${duration}. The desktop app can process videos up to ${desktopLimit}. Please upload a shorter video.`;
    }

    if (runtime === 'mobile') {
      if (durationSeconds <= limits.desktopMaxSeconds) {
        return `This video is ${duration}. Mobile uploads can process up to ${mobileLimit}. Please download the desktop app for processing up to ${desktopLimit}.`;
      }

      return `This video is ${duration}. The desktop app can process videos up to ${desktopLimit}. Please upload a shorter video.`;
    }

    return `This video is ${duration}. Desktop uploads can process up to ${desktopLimit}. Please upload a shorter video.`;
  }

  private formatLimit(seconds: number): string {
    if (!Number.isFinite(seconds) || seconds <= 0) return 'the available limit';
    if (seconds >= 60) {
      const minutes = seconds / 60;
      return `${Number.isInteger(minutes) ? minutes : minutes.toFixed(1)} minute${minutes === 1 ? '' : 's'}`;
    }
    return `${Math.round(seconds)} second${Math.round(seconds) === 1 ? '' : 's'}`;
  }
}
